import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { selectBuckets, selectCurrentDate, selectFeelData, updateBucketFeel, setFeelData } from './slice/buckets';
import { updateBucket } from './slice/functions';
import { FeelBucketItem } from './FeelBucketItem';
import { VoiceFeelCollector } from './VoiceFeelCollector';
import { CATEGORIES } from './constants';
import './FeelStep.css';

export const FeelStep = () => {
    const buckets = useSelector(selectBuckets);
    const currentDate = useSelector(selectCurrentDate);
    const feelData = useSelector(selectFeelData);
    const dispatch = useDispatch();
    const [showVoiceCollector, setShowVoiceCollector] = useState(false);

    // Save feel data to server whenever it changes
    useEffect(() => {
        const saveFeelData = async () => {
            if (Object.keys(feelData).length === 0) return;

            try {
                const server_hostname = (process.env.REACT_APP_IS_ALL_LOCAL === "true")
                    ? process.env.REACT_APP_LOCAL_SERVER_HOST
                    : process.env.REACT_APP_PROD_SERVER_HOST;

                await axios.post(server_hostname + '/feel/save', {
                    feelData
                }, {
                    withCredentials: true,
                });

                console.log('✅ Feel data saved to server');
            } catch (error) {
                console.error('Failed to save feel data:', error);
            }
        };

        // Debounce saving
        const timer = setTimeout(saveFeelData, 1000);
        return () => clearTimeout(timer);
    }, [feelData]);

    // Filter buckets for current date
    const todaysBuckets = buckets.filter(bucket => bucket.date === currentDate);

    // Group buckets by category
    const bucketsByCategory = CATEGORIES.reduce((acc, category) => {
        acc[category] = todaysBuckets.filter(bucket => bucket.category === category);
        return acc;
    }, {});

    // Count buckets that need attention (priority >= 7 and emotion set)
    const needsAttention = todaysBuckets.filter(bucket => {
        const feel = feelData[bucket.rowID]?.[currentDate];
        return feel && feel.priority >= 7 && feel.emotion;
    }).length;

    // Count buckets with feel data
    const withFeelData = todaysBuckets.filter(bucket => {
        return feelData[bucket.rowID]?.[currentDate];
    }).length;

    const handleFeelUpdate = (bucketRowID, feelEntry) => {
        dispatch(updateBucketFeel({
            bucketRowID,
            date: currentDate,
            feelEntry
        }));
    };

    const handleVoiceUpdate = (bucketRowID, feelEntry) => {
        dispatch(updateBucketFeel({
            bucketRowID,
            date: currentDate,
            feelEntry
        }));
    };

    const handleBucketStatusUpdate = (bucketRowID, newStatus) => {
        const bucket = buckets.find(b => b.rowID === bucketRowID);
        if (bucket) {
            const updatedBucket = {
                ...bucket,
                status: newStatus
            };
            updateBucket(updatedBucket, dispatch, buckets);
        }
    };

    const handleActionStatusUpdate = (bucketRowID, newActionStatus) => {
        const bucket = buckets.find(b => b.rowID === bucketRowID);
        if (bucket) {
            const updatedBucket = {
                ...bucket,
                actionStatus: newActionStatus
            };
            updateBucket(updatedBucket, dispatch, buckets);
        }
    };

    const handlePointsUpdate = (bucketRowID, newPoints) => {
        const bucket = buckets.find(b => b.rowID === bucketRowID);
        if (bucket) {
            const updatedBucket = {
                ...bucket,
                points: newPoints
            };
            updateBucket(updatedBucket, dispatch, buckets);
        }
    };

    const handleScheduleUpdate = (bucketRowID, preferredTimes) => {
        const bucket = buckets.find(b => b.rowID === bucketRowID);
        if (bucket) {
            // Parse existing time object or create new one
            let currentTime = bucket.time;
            if (typeof currentTime === 'string') {
                try {
                    currentTime = JSON.parse(currentTime);
                } catch (e) {
                    currentTime = {};
                }
            } else if (!currentTime) {
                currentTime = {};
            }

            // Update the time object with preferred times set to true, others to false
            const updatedTime = { ...currentTime };
            ['LN', 'DN', 'EM', 'MO', 'NO', 'AF', 'EV', 'NI'].forEach(timeSlot => {
                updatedTime[timeSlot] = preferredTimes.includes(timeSlot);
            });

            const updatedBucket = {
                ...bucket,
                time: updatedTime
            };
            updateBucket(updatedBucket, dispatch, buckets);
        }
    };

    const handleDifficultyUpdate = (bucketRowID, difficultyLevel) => {
        const bucket = buckets.find(b => b.rowID === bucketRowID);
        if (bucket) {
            const updatedBucket = {
                ...bucket,
                difficulty: difficultyLevel
            };
            updateBucket(updatedBucket, dispatch, buckets);
        }
    };

    return (
        <div className="feel-step-container">
            {/* {showVoiceCollector && (
                <VoiceFeelCollector
                    buckets={todaysBuckets}
                    onUpdate={handleVoiceUpdate}
                    onClose={() => setShowVoiceCollector(false)}
                />
            )} */}

            <div className="feel-step-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                        <h2 className="feel-step-title">🧘 Feel Your Day</h2>
                        <p className="feel-step-subtitle">
                            Take a moment to check in with each area of your life. How do you feel? What needs your attention?
                        </p>
                    </div>
                    <button
                        className="voice-collect-btn"
                        onClick={() => setShowVoiceCollector(true)}
                        disabled={todaysBuckets.length === 0}
                        title="Collect feel data by voice"
                    >
                        🎤 Collect by Voice
                    </button>
                </div>

                {/* Summary Stats */}
                <div className="feel-summary">
                    <div className="feel-stat">
                        <span className="feel-stat-value">{withFeelData}</span>
                        <span className="feel-stat-label">/ {todaysBuckets.length} Checked In</span>
                    </div>
                    {needsAttention > 0 && (
                        <div className="feel-stat attention">
                            <span className="feel-stat-value">⚠️ {needsAttention}</span>
                            <span className="feel-stat-label">Need Attention</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="feel-buckets-container">
                {CATEGORIES.map(category => {
                    const categoryBuckets = bucketsByCategory[category];
                    if (categoryBuckets.length === 0) return null;

                    return (
                        <div key={category} className="feel-category-section">
                            <h3 className="feel-category-title">Category {category}</h3>
                            <div className="feel-category-buckets">
                                {categoryBuckets.map(bucket => (
                                    <FeelBucketItem
                                        key={bucket.rowID}
                                        bucket={bucket}
                                        currentDate={currentDate}
                                        feelEntry={feelData[bucket.rowID]?.[currentDate]}
                                        onUpdate={(feelEntry) => handleFeelUpdate(bucket.rowID, feelEntry)}
                                        onBucketUpdate={handleBucketStatusUpdate}
                                        onActionStatusUpdate={handleActionStatusUpdate}
                                        onPointsUpdate={handlePointsUpdate}
                                        onDifficultyUpdate={handleDifficultyUpdate}
                                        onScheduleUpdate={handleScheduleUpdate}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {todaysBuckets.length === 0 && (
                <div className="feel-empty-state">
                    <p>No buckets for today. Create some buckets in the Plan step to get started!</p>
                </div>
            )}
        </div>
    );
};
